export type LiveVoiceEvent = Record<string, unknown> & { type: string };

export type LiveVoiceContextKind = 'instructions' | 'thinking' | 'commentary';

export type LiveVoiceFailure =
  | 'connection'
  | 'protocol'
  | 'capacity'
  | 'playback';

export const LIVE_VOICE_CONNECT_TIMEOUT_MS = 20_000;
export const LIVE_VOICE_FINISH_TIMEOUT_MS = 15_000;
export const LIVE_VOICE_MAX_EVENT_BYTES = 1024 * 1024;
const CONTEXT_CHUNK_BYTES = 480;
const MAX_CONTEXT_BYTES = 64 * 1024;

export function liveVoiceRecord(
  value: unknown
): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function liveVoiceString(
  value: unknown,
  maxLength = 256
): string | null {
  return typeof value === 'string' &&
    value.length > 0 &&
    value.length <= maxLength
    ? value
    : null;
}

export function parseLiveVoiceEvent(data: unknown): LiveVoiceEvent | null {
  if (
    typeof data !== 'string' ||
    new TextEncoder().encode(data).byteLength > LIVE_VOICE_MAX_EVENT_BYTES
  ) {
    return null;
  }
  try {
    const event = liveVoiceRecord(JSON.parse(data));
    return event && liveVoiceString(event.type)
      ? (event as LiveVoiceEvent)
      : null;
  } catch {
    return null;
  }
}

export function parseLiveVoiceAnswer(value: unknown): {
  sessionId: string;
  sdp: string;
} | null {
  const response = liveVoiceRecord(value);
  const session = liveVoiceRecord(response?.session);
  const transport = liveVoiceRecord(response?.transport);
  const sessionId = liveVoiceString(session?.id);
  const sdp = liveVoiceString(transport?.sdp, 128 * 1024);
  return sessionId && sdp && transport?.type === 'webrtc'
    ? { sessionId, sdp }
    : null;
}

/** Caption position is telemetry, not a user request or turn identity. */
export function liveVoiceInputTranscriptEndMs(
  event: LiveVoiceEvent
): number | null {
  if (
    event.type !== 'session.input_transcript.delta' ||
    typeof event.delta !== 'string' ||
    !event.delta.trim() ||
    typeof event.start_ms !== 'number' ||
    typeof event.end_ms !== 'number' ||
    !Number.isFinite(event.start_ms) ||
    !Number.isFinite(event.end_ms) ||
    event.start_ms < 0 ||
    event.end_ms < event.start_ms
  ) {
    return null;
  }
  return event.end_ms;
}

export function isLiveVoiceFinalEvent(
  event: LiveVoiceEvent,
  sessionId: string | null
): boolean {
  const session = liveVoiceRecord(event.session);
  const usage = liveVoiceRecord(event.usage);
  return (
    event.type === 'session.closed' &&
    sessionId !== null &&
    session?.id === sessionId &&
    typeof usage?.seconds === 'number' &&
    Number.isFinite(usage.seconds) &&
    // Closure reason is optional classification, not accounting identity.
    // Future provider reasons must not discard a valid final usage snapshot.
    usage.seconds >= 0
  );
}

/** Byte-sized chunks conservatively stay below the API's 500-token limit. */
export function chunkLiveVoiceContext(content: string): string[] {
  const encoder = new TextEncoder();
  if (encoder.encode(content).byteLength > MAX_CONTEXT_BYTES) {
    throw new Error('Live voice context exceeds the client limit');
  }
  const chunks: string[] = [];
  let chunk = '';
  let bytes = 0;
  for (const character of content) {
    const length = encoder.encode(character).byteLength;
    if (bytes + length > CONTEXT_CHUNK_BYTES) {
      chunks.push(chunk);
      chunk = '';
      bytes = 0;
    }
    chunk += character;
    bytes += length;
  }
  if (chunk) chunks.push(chunk);
  return chunks;
}

export function liveVoiceEventId(): string {
  return crypto.randomUUID();
}
